/**
 * MONSTER TOWN — VENUE & ECONOMY CONTENT. OWNED BY THE VENUE CREW.
 *
 * DATA ONLY. Every station, seat, shopfront and price for venue #1 lives here,
 * plus the `VenueDef` that tells the (venue-agnostic) service loop how to run
 * it. Venue #2 — zoo, garden, park — is another `defineVenue()` and another
 * block of `definePlaceable()`s in this file. No engine code changes.
 *
 * THE PRICE RULES (brief, Economy):
 *  - basics are FREE FOREVER: the shopfront, the door, the counter, cones,
 *    vanilla + strawberry, tables, stools, the bin. A kid can always open a
 *    shop and serve someone, with zero coins, on day one.
 *  - you only ever buy the FUN stuff: extra flavours, toppings, cups, the
 *    display case, the sundae pedestal.
 *  - coins come easily, so every price here is a few minutes of serving.
 */

import { FOOT_1x1, FOOT_3x2, type Cell } from '../core/grid'
import { definePlaceable } from '../core/placeable'
import type { PaletteName } from '../core/palette'
import type { PlaceableId, SceneId } from '../core/types'
import {
  coinShopFrontArt,
  exitDoorArt,
  iceCreamFrontArt,
  stackArt,
  toppingJarArt,
  tubArt,
} from '../venue/fixtures'
import { defineVenue, type VenueDef, type VenueItem } from '../venue/types'
import {
  TOWN_ECONOMY_CONTENT,
  TOWN_ECONOMY_FREE_IDS,
  findCoin,
  jobBoard,
  shopCounter,
  shopPlinth,
  venuePlant,
  venueRug,
} from './townEconomy'

// Re-exported so the rest of the crew keeps one import path for content ids.
export { findCoin, jobBoard, shopCounter, shopPlinth, venuePlant, venueRug }

// Scene ids and the ice cream menu live in `menu.ts` so the fixture files can
// read them without importing this file back. Re-exported here so the rest of
// the crew keeps one import path.
export {
  ICE_CREAM_ITEMS,
  ICE_CREAM_SCENE,
  ICE_CREAM_VENUE,
  RESTAURANT_SCENE,
  RESTAURANT_VENUE,
  TOWN_SHOP_SCENE,
} from './menu'
import { ICE_CREAM_ITEMS, ICE_CREAM_SCENE, ICE_CREAM_VENUE, TOWN_SHOP_SCENE } from './menu'
import {
  VENUE_FLOOR_CONTENT,
  VENUE_FLOOR_FREE_IDS,
  venueBin,
  venueCounter,
  venueDisplay,
  venueMenu,
  venueSpecial,
  venueStool,
  venueTable,
} from './venueFloor'

export { venueBin, venueCounter, venueDisplay, venueMenu, venueSpecial, venueStool, venueTable }

const TOWN = 'town' as SceneId
const id = (s: string): PlaceableId => s as PlaceableId

// ---------------------------------------------------------------------------
// station factories — one line of data per station
// ---------------------------------------------------------------------------

function tub(defId: string, itemId: string, color: PaletteName, cost: number, tier?: string) {
  return definePlaceable<{ scoops: number }>({
    id: id(defId),
    devName: `Ice cream tub (${itemId})`,
    category: 'venueFixture',
    scale: 'interior',
    footprint: FOOT_1x1,
    cost,
    ...(tier === undefined ? {} : { tier }),
    stackable: false,
    walkable: false,
    // The tile picture is the only label a kid gets (LAW #4), so the flavour
    // colour is DATA on the def, not something the drawer has to guess.
    tints: [color, 'cone'],
    tags: ['venue', 'station', 'scoop'],
    initialState: () => ({ scoops: 0 }),
    buildArt: (ctx) => tubArt(ctx, color),
    // LAW #1: the scoop dips, the dome dents, and a scoop lands on your tray.
    interact: (ctx) => ({
      feedback: [
        { kind: 'anim', clip: 'scoop', seconds: 0.4, target: 'lever' },
        { kind: 'anim', clip: 'squish', seconds: 0.3, target: 'dome' },
        { kind: 'particles', effect: 'sparkle', count: 5 },
        { kind: 'spawn', what: `venue:add:${itemId}` },
        { kind: 'sound', cue: 'scoop_pop' },
        { kind: 'state', patch: { scoops: ctx.state.scoops + 1 } },
      ],
      cooldownMs: 160,
    }),
  })
}

function topping(defId: string, itemId: string, cost: number, tier?: string) {
  const item = ICE_CREAM_ITEMS.find((i) => i.id === itemId) as VenueItem
  return definePlaceable<{ used: number }>({
    id: id(defId),
    devName: `Topping jar (${itemId})`,
    category: 'venueFixture',
    scale: 'interior',
    footprint: FOOT_1x1,
    cost,
    ...(tier === undefined ? {} : { tier }),
    stackable: false,
    walkable: false,
    tints: [item.color, 'plate'],
    tags: ['venue', 'station', 'topping'],
    initialState: () => ({ used: 0 }),
    buildArt: (ctx) => toppingJarArt(ctx, item),
    interact: (ctx) => ({
      feedback: [
        { kind: 'anim', clip: 'pop', seconds: 0.35, target: 'lid' },
        { kind: 'particles', effect: 'sparkle', count: 6 },
        { kind: 'spawn', what: `venue:add:${itemId}` },
        { kind: 'sound', cue: 'topping_shake' },
        { kind: 'state', patch: { used: ctx.state.used + 1 } },
      ],
      cooldownMs: 160,
    }),
  })
}

function baseStack(defId: string, itemId: string, kind: 'cone' | 'cup', cost: number, tier?: string) {
  return definePlaceable<{ taken: number }>({
    id: id(defId),
    devName: `${kind} stack`,
    category: 'venueFixture',
    scale: 'interior',
    footprint: FOOT_1x1,
    cost,
    ...(tier === undefined ? {} : { tier }),
    stackable: false,
    walkable: false,
    tints: kind === 'cone' ? ['cone', 'iceVanilla'] : ['plate', 'glassDark'],
    tags: ['venue', 'station', 'base'],
    initialState: () => ({ taken: 0 }),
    buildArt: (ctx) => stackArt(ctx, kind),
    interact: (ctx) => ({
      feedback: [
        { kind: 'anim', clip: 'lift', seconds: 0.4, target: 'holder' },
        { kind: 'particles', effect: 'sparkle', count: 4 },
        { kind: 'spawn', what: `venue:add:${itemId}` },
        { kind: 'sound', cue: 'pick_up' },
        { kind: 'state', patch: { taken: ctx.state.taken + 1 } },
      ],
      cooldownMs: 160,
    }),
  })
}

// ---------------------------------------------------------------------------
// doors — the reason building has a purpose: you walk into what you built
// ---------------------------------------------------------------------------

export const venueShopFront = definePlaceable<{ visits: number }>({
  id: id('venue_shopfront'),
  devName: 'Ice cream shop front',
  category: 'townProp',
  scale: 'exterior',
  footprint: FOOT_3x2,
  cost: 0,
  stackable: false,
  walkable: false,
  tags: ['venue', 'door', 'starter'],
  initialState: () => ({ visits: 0 }),
  buildArt: (ctx) => iceCreamFrontArt(ctx),
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'doorSwing', seconds: 0.45, target: 'door' },
      { kind: 'particles', effect: 'sparkle', count: 8 },
      { kind: 'sound', cue: 'door_open' },
      { kind: 'state', patch: { visits: ctx.state.visits + 1 } },
      { kind: 'scene', to: ICE_CREAM_SCENE },
    ],
    cooldownMs: 600,
  }),
})

export const townShopFront = definePlaceable<{ visits: number }>({
  id: id('shop_front'),
  devName: 'Town shop front',
  category: 'townProp',
  scale: 'exterior',
  footprint: FOOT_3x2,
  cost: 0,
  stackable: false,
  walkable: false,
  tags: ['shop', 'door', 'starter'],
  initialState: () => ({ visits: 0 }),
  buildArt: (ctx) => coinShopFrontArt(ctx),
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'doorSwing', seconds: 0.45, target: 'door' },
      { kind: 'particles', effect: 'coinSparkle', count: 8 },
      { kind: 'sound', cue: 'door_open' },
      { kind: 'state', patch: { visits: ctx.state.visits + 1 } },
      { kind: 'scene', to: TOWN_SHOP_SCENE },
    ],
    cooldownMs: 600,
  }),
})

export const venueExit = definePlaceable<{ used: number }>({
  id: id('venue_exit'),
  devName: 'Way out',
  category: 'door',
  scale: 'interior',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: true,
  tags: ['venue', 'door'],
  initialState: () => ({ used: 0 }),
  buildArt: (ctx) => exitDoorArt(ctx),
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'doorSwing', seconds: 0.4, target: 'door' },
      { kind: 'particles', effect: 'sparkle', count: 5 },
      { kind: 'sound', cue: 'door_open' },
      { kind: 'state', patch: { used: ctx.state.used + 1 } },
      { kind: 'scene', to: TOWN },
    ],
    cooldownMs: 600,
  }),
})

// ---------------------------------------------------------------------------
// generated stations
// ---------------------------------------------------------------------------

export const venueCones = baseStack('venue_cones', 'base_cone', 'cone', 0)
export const venueCups = baseStack('venue_cups', 'base_cup', 'cup', 30)

export const tubVanilla = tub('venue_tub_vanilla', 'scoop_vanilla', 'iceVanilla', 0)
export const tubStrawberry = tub('venue_tub_strawberry', 'scoop_strawberry', 'iceStrawberry', 0)
export const tubChocolate = tub('venue_tub_chocolate', 'scoop_chocolate', 'iceChocolate', 45)
export const tubMint = tub('venue_tub_mint', 'scoop_mint', 'iceMint', 70, 'tier1')
export const tubBlueberry = tub('venue_tub_blueberry', 'scoop_blueberry', 'iceBlueberry', 90, 'tier1')

export const topCherry = topping('venue_top_cherry', 'top_cherry', 55)
export const topSprinkles = topping('venue_top_sprinkles', 'top_sprinkles', 80, 'tier1')
export const topSauce = topping('venue_top_sauce', 'top_sauce', 110, 'tier2')

// ---------------------------------------------------------------------------
// THE VENUE DEFINITION
// ---------------------------------------------------------------------------

const cell = (gx: number, gz: number): Cell => ({ gx, gy: 0, gz })

/**
 * Where the job board is seeded on a brand new town — off to one side of the
 * buildable plot so it never lands in the middle of a kid's build. Movable like
 * anything else. The shopfront placeables are NOT seeded: the world crew's
 * buildings already have front doors into these interiors, and a duplicate
 * shop in the middle of the plot would be an eyesore. They stay in the
 * catalogue so a kid can put up their own second shop if they want one.
 */
export const JOB_BOARD_CELL: Cell = cell(4, 19)

/**
 * The ice cream shop. The interior grid's origin is chosen so the inside door
 * lands almost exactly under the town shopfront's door in world space — walking
 * through therefore puts the monster right inside the doorway instead of
 * teleporting it across the room.
 */
export const iceCreamShop: VenueDef = defineVenue({
  id: ICE_CREAM_VENUE,
  kind: 'iceCream',
  sceneId: ICE_CREAM_SCENE,
  devName: 'Ice cream shop',
  // 12 x 9 tiles is deliberately SMALL. A big room under a fixed iso camera
  // reads as an empty plate: the furniture ends up tiny and the shop feels
  // deserted. Cosy and full beats spacious every time at this age.
  // The origin puts cell (6,8) — the way out — under the world crew's ice
  // cream shop front door at world (-13, 32), so walking through the town-side
  // door leaves you standing in the doorway rather than inside a wall.
  grid: { id: 'venue_iceCream', scale: 'interior', width: 12, depth: 9, levels: 4, originX: -19.5, originZ: 23.5 },
  /**
   * STRAWBERRY WALLS, MINT FLOOR, TEAL TRIM — an ice cream parlour, and a room
   * that cannot be confused with the thing it is standing in (venue fix, r3).
   *
   * The walls used to be `wallCream`, which is within a hair of the warm ring
   * of the interior surround they sit on. At the working zoom the two met with
   * no edge between them, so the right-hand half of the frame read as one
   * "untextured brown gradient with windows floating on nothing" — the windows
   * were on a perfectly real wall you simply could not see. Pink separates from
   * the surround at a glance, and it says ICE CREAM before anything else does.
   */
  shell: {
    wall: 'wallPink',
    wallTrim: 'roofTeal',
    floor: 'floorTile',
    floorAlt: 'wallMint',
    roofBeam: 'woodLight',
    rug: 'wallLilac',
  },
  doorCell: cell(6, 8),
  items: ICE_CREAM_ITEMS,
  seatDefIds: [venueTable.id, venueStool.id],
  maxScoops: 3,
  // The shop opens with everything you need to serve someone on day one, and
  // nothing you have to buy first: cones, two flavours, a counter, a bin, and
  // three places to sit. Free forever, all of it.
  starter: [
    { defId: venueExit.id, anchor: cell(6, 8) },
    { defId: venueCounter.id, anchor: cell(5, 3) },
    { defId: tubStrawberry.id, anchor: cell(3, 1) },
    { defId: tubVanilla.id, anchor: cell(4, 1) },
    { defId: venueCones.id, anchor: cell(7, 1) },
    { defId: venueBin.id, anchor: cell(9, 1) },
    { defId: venueTable.id, anchor: cell(1, 5) },
    { defId: venueTable.id, anchor: cell(8, 5) },
    { defId: venueStool.id, anchor: cell(5, 6) },
  ],
})

// ---------------------------------------------------------------------------
// registration exports — `src/content/index.ts` spreads these
// ---------------------------------------------------------------------------

export const VENUE_CONTENT = [
  venueShopFront,
  townShopFront,
  venueExit,
  ...VENUE_FLOOR_CONTENT,
  ...TOWN_ECONOMY_CONTENT,
  venueCones,
  venueCups,
  tubVanilla,
  tubStrawberry,
  tubChocolate,
  tubMint,
  tubBlueberry,
  topCherry,
  topSprinkles,
  topSauce,
]

/** Never chargeable, whatever a def says. A kid can always open the shop. */
export const VENUE_FREE_IDS: PlaceableId[] = [
  venueShopFront.id,
  townShopFront.id,
  ...VENUE_FLOOR_FREE_IDS,
  venueCones.id,
  tubVanilla.id,
  tubStrawberry.id,
  ...TOWN_ECONOMY_FREE_IDS,
]

export const VENUE_DEFS: VenueDef[] = [iceCreamShop]
